import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SeoManager } from "@/components/SeoManager";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Inventory from "@/pages/Inventory";
import VehicleDetail from "@/pages/VehicleDetail";
import DealerLinkRequest from "@/pages/DealerLinkRequest";
import Concierge from "@/pages/Concierge";
import BuyerDashboard from "@/pages/BuyerDashboard";
import Profile from "@/pages/Profile";
import RequestDetail from "@/pages/RequestDetail";
import StaffDashboard from "@/pages/staff/StaffDashboard";
import StaffTrash from "@/pages/staff/StaffTrash";
import StaffMessages from "@/pages/staff/StaffMessages";
import StaffPrequal from "@/pages/staff/StaffPrequal";
import Prequal from "@/pages/Prequal";
import StaffContent from "@/pages/staff/StaffContent";
import StaffPromotions from "@/pages/staff/StaffPromotions";
import Specials from "@/pages/Specials";
import Policy from "@/pages/Policy";
import Contact from "@/pages/Contact";
import PartnerSpecials from "@/pages/partner/PartnerSpecials";
import StaffInventory from "@/pages/staff/StaffInventory";
import StaffRequestDetail from "@/pages/staff/StaffRequestDetail";
import StaffDuplicates from "@/pages/staff/StaffDuplicates";
import StaffPartners from "@/pages/staff/StaffPartners";
import StaffTeam from "@/pages/staff/StaffTeam";
import PartnerInventory from "@/pages/partner/PartnerInventory";
import PartnerAnalytics from "@/pages/partner/PartnerAnalytics";
import PartnerProfile from "@/pages/partner/PartnerProfile";
import DealerApplication from "@/pages/DealerApplication";

function Loader() {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 dark:border-slate-800 border-t-blue-600" />
    </div>
  );
}

const STAFF_ROLES = ["super_admin", "broker", "sales", "staff", "admin"];
const roleHome = (role) => (role === "partner" ? "/partner" : STAFF_ROLES.includes(role) ? "/staff" : "/dashboard");

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  return children;
}

function AuthCallback() {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const run = async () => {
      const hash = window.location.hash;
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (!sid) { navigate("/login"); return; }
      try {
        const user = await exchangeSession(sid);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(roleHome(user.role), { replace: true });
      } catch {
        navigate("/login");
      }
    };
    run();
  }, []);
  return <Loader />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <SeoManager />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Auth />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/specials" element={<Specials />} />
        <Route path="/privacy" element={<Policy kind="privacy" />} />
        <Route path="/terms" element={<Policy kind="terms" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/dealer-application/:linkToken" element={<DealerApplication />} />
        <Route path="/vehicles/:id" element={<VehicleDetail />} />
        <Route path="/request/new" element={<Protected><DealerLinkRequest /></Protected>} />
        <Route path="/concierge" element={<Protected><Concierge /></Protected>} />
        <Route path="/dashboard" element={<Protected><BuyerDashboard /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/prequal" element={<Protected><Prequal /></Protected>} />
        <Route path="/staff/prequal" element={<Protected roles={STAFF_ROLES}><StaffPrequal /></Protected>} />
        <Route path="/requests/:id" element={<Protected><RequestDetail /></Protected>} />
        <Route path="/staff" element={<Protected roles={STAFF_ROLES}><StaffDashboard /></Protected>} />
        <Route path="/staff/trash" element={<Protected roles={STAFF_ROLES}><StaffTrash /></Protected>} />
        <Route path="/staff/messages" element={<Protected roles={STAFF_ROLES}><StaffMessages /></Protected>} />
        <Route path="/staff/inventory" element={<Protected roles={STAFF_ROLES}><StaffInventory /></Protected>} />
        <Route path="/staff/requests/:id" element={<Protected roles={STAFF_ROLES}><StaffRequestDetail /></Protected>} />
        <Route path="/staff/duplicates" element={<Protected roles={STAFF_ROLES}><StaffDuplicates /></Protected>} />
        <Route path="/staff/partners" element={<Protected roles={STAFF_ROLES}><StaffPartners /></Protected>} />
        <Route path="/staff/team" element={<Protected roles={["super_admin", "broker", "admin"]}><StaffTeam /></Protected>} />
        <Route path="/staff/content" element={<Protected roles={["super_admin", "broker", "admin"]}><StaffContent /></Protected>} />
        <Route path="/staff/promotions" element={<Protected roles={["super_admin", "broker", "admin"]}><StaffPromotions /></Protected>} />
        <Route path="/partner" element={<Protected roles={["partner"]}><PartnerInventory /></Protected>} />
        <Route path="/partner/analytics" element={<Protected roles={["partner"]}><PartnerAnalytics /></Protected>} />
        <Route path="/partner/specials" element={<Protected roles={["partner"]}><PartnerSpecials /></Protected>} />
        <Route path="/partner/profile" element={<Protected roles={["partner"]}><PartnerProfile /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
